import { BrowserMultiFormatReader } from '@zxing/browser';
import { useEffect, useRef, useState } from 'react';
import { FaBarcode, FaCamera, FaTimes, FaSearch, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useTranslation } from './hooks/useTranslation';
import { analyzeImageWithPrompt } from './services/visionService';
import { apiFetch } from './services/apiService';

// KI-Prompt: findet ALLE Sendungs-/Trackingnummern im Foto → reines JSON-Array.
const TRACKING_PROMPT =
  'Du siehst das Foto von einem oder mehreren Paketetiketten. Finde ALLE Sendungs-/' +
  'Trackingnummern (Paketnummern). Gib NUR ein reines JSON-Array der Nummern als Strings ' +
  'zurück, ohne Leerzeichen, ohne weitere Erklärung, z. B. ["00340434161094022516","JD014600..."]. ' +
  'KEINE Telefonnummern, Postleitzahlen, Preise, Kunden- oder Referenznummern, die keine ' +
  'Sendungsnummern sind. Wenn keine gefunden: leeres Array [].';

// Parst Geminis Antwort robust zu einer Liste eindeutiger Nummern.
function parseTrackingNumbers(text) {
  if (!text) return [];
  const t = String(text).trim().replace(/^```(?:json)?/i, '').replace(/```$/, '').trim();
  let arr = [];
  try {
    const parsed = JSON.parse(t);
    if (Array.isArray(parsed)) arr = parsed;
    else if (parsed && Array.isArray(parsed.trackingNumbers)) arr = parsed.trackingNumbers;
  } catch {
    arr = t.match(/[A-Z0-9]{8,}/gi) || [];
  }
  const seen = new Set();
  return arr
    .map((x) => String(x).replace(/\s+/g, ''))
    .filter((x) => x.length >= 8 && !seen.has(x) && seen.add(x))
    .slice(0, 12);
}

const TrackingForm = ({ darkMode = false, colors, texts, lang }) => {
  const { t } = useTranslation();
  const [trackingNumber, setTrackingNumber] = useState('');
  const [trackingInfo, setTrackingInfo] = useState(null);
  const [error, setError] = useState(null);
  // Kein Ergebnis in der App: Paketdienst ohne Abfrage (nur Link) oder nichts gefunden → Links zu den Paketdiensten
  const [trackHint, setTrackHint] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ocrError, setOcrError] = useState(null);

  // KI-Erkennung
  const [trackingCandidates, setTrackingCandidates] = useState([]);
  const [showBarcodeScanner, setShowBarcodeScanner] = useState(false);
  
  const videoRef = useRef(null);
  const codeReaderRef = useRef(null);
  const streamRef = useRef(null);
  
  // 🛡️ RACE CONDITION FIX: Track ob Komponente noch mounted ist
  const isMountedRef = useRef(true);

  // Dynamische Farben basierend auf Theme
  const themeColors = {
    background: darkMode ? '#1e1e1e' : '#ffffff',
    cardBg: darkMode ? '#2d323c' : '#f8f9fa',
    text: darkMode ? '#ffffff' : '#212529',
    textSecondary: darkMode ? '#adb5bd' : '#6c757d',
    border: darkMode ? '#495057' : '#dee2e6',
    success: '#28a745',
    danger: '#dc3545',
    primary: '#007bff',
    warning: '#ffc107'
  };

  const stopBarcodeScanner = () => {
    try {
      if (codeReaderRef.current) {
        codeReaderRef.current.reset();
        codeReaderRef.current = null;
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
      
      if (videoRef.current) {
        videoRef.current.srcObject = null;
      }
    } catch (error) {
      console.error('Fehler beim Stoppen des Scanners:', error);
    } finally {
      setShowBarcodeScanner(false);
    }
  };

  const startBarcodeScanner = async () => {
    // 🛡️ RACE CONDITION FIX: Prüfe ob noch mounted
    if (!isMountedRef.current) return;
    
    try {
      stopBarcodeScanner();
      setShowBarcodeScanner(true);
      
      setTimeout(async () => {
        // 🛡️ RACE CONDITION FIX: Prüfe ob noch mounted
        if (!isMountedRef.current) return;
        
        try {
          const codeReader = new BrowserMultiFormatReader();
          codeReaderRef.current = codeReader;
          
          const result = await codeReader.decodeOnceFromVideoDevice(undefined, videoRef.current);
          
          // 🛡️ RACE CONDITION FIX: Prüfe erneut nach await
          if (!isMountedRef.current) return;
          
          setTrackingNumber(result.text);
          stopBarcodeScanner();
          
        } catch (err) {
          console.error('Scanner-Fehler:', err);
          if (isMountedRef.current) stopBarcodeScanner();
        }
      }, 200);
    } catch (error) {
      console.error('Fehler beim Starten des Scanners:', error);
      if (isMountedRef.current) setShowBarcodeScanner(false);
    }
  };

  useEffect(() => {
    // 🛡️ RACE CONDITION FIX: Setze mounted flag
    isMountedRef.current = true;
    
    const handleVisibilityChange = () => {
      if (document.hidden && showBarcodeScanner) {
        console.log('Tab versteckt - stoppe Scanner');
        stopBarcodeScanner();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      isMountedRef.current = false;
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopBarcodeScanner();
    };
  }, [showBarcodeScanner]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !isMountedRef.current) return;

    setLoading(true);
    setOcrError(null);
    setTrackingCandidates([]);
    setTrackingInfo(null);
    setTrackHint(null);
    setError(null);

    try {
      // Bild als Base64 (ohne data:-Prefix) an Gemini schicken
      const base64full = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result));
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const base64 = base64full.split(',')[1];

      const resp = await analyzeImageWithPrompt(base64, TRACKING_PROMPT);
      if (!isMountedRef.current) return;

      const nums = parseTrackingNumbers(resp?.text);
      if (nums.length === 0) {
        setOcrError(t('tracking.noTextRecognized') || 'Keine Sendungsnummer erkannt — bitte näher und schärfer fotografieren.');
      } else if (nums.length === 1) {
        // Nur eine gefunden → direkt verfolgen
        setTrackingNumber(nums[0]);
        doTrack(nums[0]);
      } else {
        // Mehrere gefunden → zur Auswahl anbieten (Antippen startet die Abfrage)
        setTrackingCandidates(nums);
      }
    } catch (err) {
      console.error('KI-Tracking Fehler:', err);
      if (isMountedRef.current) setOcrError(t('tracking.ocrError') || 'Analyse fehlgeschlagen. Bitte erneut versuchen.');
    }
    if (isMountedRef.current) setLoading(false);
  };


  // Fragt den Server nach einer konkreten Nummer (aus Formular ODER Kandidaten-Tipp). Der Server erkennt den
  // Paketdienst: DHL und Vinted Go werden direkt abgefragt, für die anderen kommt der Link zu deren Seite.
  const doTrack = async (number) => {
    const num = (number || '').trim();
    if (!num || !isMountedRef.current) return;
    setLoading(true);
    setError(null);
    setTrackingInfo(null);
    setTrackHint(null);
    try {
      const r = await apiFetch('/tracking', { method: 'POST', body: JSON.stringify({ nummer: num, sprache: lang }), timeout: 30000 });
      if (!isMountedRef.current) return;
      if (r && r.ok && r.inApp) setTrackingInfo(r);
      else setTrackHint(r || { ok: false, reason: 'fehler', links: [] });
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.status === 429 ? t('tracking.tooMany') : (err.message || t('tracking.fetchError')));
      }
    }
    if (isMountedRef.current) setLoading(false);
  };

  // DHL liefert ISO-Zeiten, Vinted Go ausgeschriebene („9:12 AM, Tuesday, September 29") — die bleiben, wie sie sind
  const formatZeit = (z) => (/^\d{4}-\d{2}-\d{2}T/.test(String(z || ''))
    ? new Date(z).toLocaleString(lang || undefined, { dateStyle: 'medium', timeStyle: 'short' })
    : String(z || ''));

  const handleSubmit = (e) => {
    e.preventDefault();
    doTrack(trackingNumber);
  };


  return (
    <div style={{ 
      padding: '16px', 
      maxWidth: '600px', 
      margin: '0 auto',
      background: themeColors.background,
      color: themeColors.text,
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{
        textAlign: 'center',
        marginBottom: '24px',
        padding: '16px',
        background: themeColors.cardBg,
        borderRadius: '12px',
        border: `1px solid ${themeColors.border}`
      }}>
        <h1 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '24px', 
          fontWeight: 'bold',
          color: themeColors.primary 
        }}>
          {t('tracking.title')}
        </h1>
        <p style={{ 
          margin: 0, 
          color: themeColors.textSecondary,
          fontSize: '14px' 
        }}>
          {t('tracking.subtitle')}
        </p>
        <p style={{ 
          margin: '8px 0 0 0', 
          color: themeColors.warning,
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {t('tracking.carrierHint')}
        </p>
      </div>

      {/* Scanner Buttons */}
      <div style={{
        background: themeColors.cardBg,
        borderRadius: '12px',
        padding: '20px',
        marginBottom: '20px',
        border: `1px solid ${themeColors.border}`,
        textAlign: 'center'
      }}>
        <h3 style={{ 
          margin: '0 0 16px 0', 
          color: themeColors.text,
          fontSize: '16px' 
        }}>
          {t('tracking.captureNumber')}
        </h3>
        
        <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', flexWrap: 'wrap' }}>
          <input
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoUpload}
            style={{ display: 'none' }}
            id="tracking-photo-input"
          />
          
          <button
            type="button"
            onClick={() => document.getElementById('tracking-photo-input').click()}
            style={{
              background: 'linear-gradient(135deg, #007bff, #0056b3)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              width: '60px',
              height: '60px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              boxShadow: '0 4px 12px rgba(0, 123, 255, 0.3)',
              transition: 'all 0.2s ease'
            }}
            title={t('tracking.takePhoto')}
            onMouseOver={e => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={e => e.target.style.transform = 'scale(1)'}
          >
            <FaCamera />
          </button>
          
          <button
            type="button"
            onClick={startBarcodeScanner}
            style={{
              background: 'linear-gradient(135deg, #28a745, #1e7e34)',
              color: '#fff',
              border: 'none',
              borderRadius: '12px',
              width: '60px',
              height: '60px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '24px',
              boxShadow: '0 4px 12px rgba(40, 167, 69, 0.3)',
              transition: 'all 0.2s ease'
            }}
            title={t('tracking.scanBarcode')}
            onMouseOver={e => e.target.style.transform = 'scale(1.05)'}
            onMouseOut={e => e.target.style.transform = 'scale(1)'}
          >
            <FaBarcode />
          </button>
        </div>
        
        <div style={{ 
          marginTop: '12px', 
          display: 'flex', 
          justifyContent: 'center', 
          gap: '20px',
          fontSize: '12px',
          color: themeColors.textSecondary
        }}>
          <span>{t('tracking.photo')}</span>
          <span>{t('tracking.barcode')}</span>
        </div>
      </div>

      {/* Barcode Scanner Modal */}
      {showBarcodeScanner && (
        <div style={{
          position: 'fixed', 
          top: 0, 
          left: 0, 
          width: '100vw', 
          height: '100vh',
          background: 'rgba(0,0,0,0.9)', 
          zIndex: 1000, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center'
        }}>
          <div style={{ 
            background: themeColors.background, 
            padding: '24px', 
            borderRadius: '16px', 
            maxWidth: '90vw',
            maxHeight: '90vh',
            boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '16px' 
            }}>
              <h3 style={{ margin: 0, color: themeColors.text }}>{t('tracking.barcodeScanner')}</h3>
              <button 
                onClick={stopBarcodeScanner}
                style={{
                  background: 'none',
                  border: 'none',
                  color: themeColors.text,
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                <FaTimes />
              </button>
            </div>
            
            <video 
              ref={videoRef} 
              style={{ 
                width: '100%', 
                maxWidth: '400px', 
                height: '250px',
                borderRadius: '8px',
                background: '#000'
              }} 
            />
            
            <div style={{ marginTop: '16px', textAlign: 'center' }}>
              <button 
                onClick={stopBarcodeScanner}
                style={{
                  padding: '12px 24px',
                  background: '#dc3545',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: 'bold'
                }}
              >
                {t('tracking.close')}
              </button>
            </div>
          </div>
        </div>
      )}


      {/* OCR Status */}
      {loading && (
        <div style={{
          background: themeColors.cardBg,
          border: `1px solid ${themeColors.primary}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <div style={{ color: themeColors.primary, fontSize: '20px' }}>⏳</div>
          <span style={{ color: themeColors.primary }}>{t('tracking.ocrRunning')}</span>
        </div>
      )}

      {ocrError && (
        <div style={{
          background: 'rgba(220, 53, 69, 0.1)',
          border: `1px solid ${themeColors.danger}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FaTimesCircle style={{ color: themeColors.danger, fontSize: '20px' }} />
          <span style={{ color: themeColors.danger }}>{ocrError}</span>
        </div>
      )}

      {/* Tracking Candidates */}
      {trackingCandidates.length > 0 && (
        <div style={{
          background: themeColors.cardBg,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: `2px solid ${themeColors.primary}`,
          boxShadow: '0 4px 12px rgba(0, 123, 255, 0.1)'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px', 
            marginBottom: '16px' 
          }}>
            <span style={{ fontSize: '20px' }}>🎯</span>
            <h4 style={{ margin: 0, color: themeColors.text }}>
              {t('tracking.selectTrackingNumber')}
            </h4>
          </div>
          
          {/* Scrollbare Liste mit besserer Darstellung */}
          <div style={{ 
            maxHeight: '300px', 
            overflowY: 'auto',
            display: 'grid',
            gap: '8px'
          }}>
            {trackingCandidates.map((candidate, index) => {
              // Prüfe ob es wie eine Tracking-Nummer aussieht
              const looksLikeTracking = /^[A-Z0-9]{8,}$/.test(candidate.replace(/\s/g, '')) || 
                                      /^[0-9]{10,}$/.test(candidate) ||
                                      candidate.includes('1Z') ||
                                      candidate.length >= 12;
              
              return (
                <button
                  key={`${candidate}-${index}`}
                  style={{
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: looksLikeTracking 
                      ? `2px solid ${themeColors.success}` 
                      : `1px solid ${themeColors.border}`,
                    background: looksLikeTracking 
                      ? 'rgba(40, 167, 69, 0.1)' 
                      : themeColors.background,
                    color: themeColors.text,
                    cursor: 'pointer',
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    textAlign: 'left',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onClick={() => {
                    setTrackingNumber(candidate);
                    setTrackingCandidates([]);
                    setOcrError(null);
                    doTrack(candidate); // Antippen startet direkt die Abfrage
                  }}
                  onMouseOver={e => {
                    e.target.style.background = themeColors.primary;
                    e.target.style.color = '#fff';
                  }}
                  onMouseOut={e => {
                    e.target.style.background = looksLikeTracking 
                      ? 'rgba(40, 167, 69, 0.1)' 
                      : themeColors.background;
                    e.target.style.color = themeColors.text;
                  }}
                >
                  {/* Icon für wahrscheinliche Tracking-Nummern */}
                  {looksLikeTracking && (
                    <span style={{ color: themeColors.success, fontSize: '16px' }}>📦</span>
                  )}
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: looksLikeTracking ? 'bold' : 'normal' }}>
                      {candidate}
                    </div>
                    {looksLikeTracking && (
                      <div style={{ 
                        fontSize: '11px', 
                        color: themeColors.success,
                        marginTop: '2px' 
                      }}>
                        {t('tracking.looksLikeTracking')}
                      </div>
                    )}
                  </div>
                  
                  {/* Zeichen-Anzahl */}
                  <div style={{ 
                    fontSize: '11px', 
                    color: themeColors.textSecondary,
                    opacity: 0.7 
                  }}>
                    {candidate.length} {t('tracking.characters')}
                  </div>
                </button>
              );
            })}
          </div>
          
          {/* Buttons zum Verwerfen */}
          <div style={{ 
            marginTop: '16px', 
            display: 'flex', 
            gap: '8px', 
            justifyContent: 'center' 
          }}>
            <button
              onClick={() => {
                setTrackingCandidates([]);
                setOcrError(null);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: `1px solid ${themeColors.textSecondary}`,
                background: 'none',
                color: themeColors.textSecondary,
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {t('tracking.differentSelection')}
            </button>
            
            <button
              onClick={() => {
                setTrackingCandidates([]);
                setOcrError(null);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '6px',
                border: 'none',
                background: themeColors.danger,
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              {t('tracking.takeNewPhoto')}
            </button>
          </div>
        </div>
      )}

      {/* Main Form */}
      <form onSubmit={handleSubmit}>
        <div style={{
          background: themeColors.cardBg,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: `1px solid ${themeColors.border}`
        }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: 'bold',
            color: themeColors.text 
          }}>
            {t('tracking.enterTrackingNumber')}
          </label>
          
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              type="text"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              required
              placeholder={t('tracking.trackingPlaceholder')}
              style={{
                flex: 1,
                background: themeColors.background,
                border: `2px solid ${themeColors.border}`,
                borderRadius: '8px',
                padding: '12px 16px',
                fontSize: '16px',
                color: themeColors.text,
                outline: 'none',
                transition: 'border-color 0.2s ease'
              }}
              onFocus={e => e.target.style.borderColor = themeColors.primary}
              onBlur={e => e.target.style.borderColor = themeColors.border}
            />
            
            <button
              type="submit"
              disabled={loading || !trackingNumber.trim()}
              style={{
                background: loading || !trackingNumber.trim() 
                  ? themeColors.textSecondary 
                  : 'linear-gradient(135deg, #28a745, #1e7e34)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                padding: '12px 20px',
                fontWeight: 'bold',
                fontSize: '16px',
                cursor: loading || !trackingNumber.trim() ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                minWidth: '120px',
                justifyContent: 'center'
              }}
            >
              {loading ? '⏳' : <FaSearch />}
              {loading ? t('tracking.searching') : t('tracking.track')}
            </button>
          </div>
        </div>
      </form>

      {/* Error Display */}
      {error && (
        <div style={{
          background: 'rgba(220, 53, 69, 0.1)',
          border: `1px solid ${themeColors.danger}`,
          borderRadius: '12px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <FaTimesCircle style={{ color: themeColors.danger, fontSize: '20px' }} />
          <div>
            <div style={{ fontWeight: 'bold', color: themeColors.danger }}>{t('tracking.error')}</div>
            <div style={{ color: themeColors.danger, fontSize: '14px' }}>{error}</div>
          </div>
        </div>
      )}

      {/* Kein Ergebnis in der App: Link(s) zur Sendungsverfolgung der Paketdienste */}
      {trackHint && (
        <div data-testid="tracking-hint" data-dienst={trackHint.dienst || ''} style={{
          background: themeColors.cardBg,
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '20px',
          border: `1px solid ${trackHint.ok ? themeColors.primary : themeColors.warning}`
        }}>
          <div style={{ color: themeColors.text, fontSize: '14px', lineHeight: 1.5, marginBottom: '14px' }}>
            {trackHint.ok ? t('tracking.linkOnly') : trackHint.reason === 'fehler' ? t('tracking.fetchError') : t('tracking.notFound')}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {(trackHint.links || []).map((l, index) => {
              const haupt = trackHint.ok && index === 0; // der erkannte Paketdienst
              return (
                <a
                  key={l.dienst}
                  data-testid={`tracking-link-${l.dienst}`}
                  href={l.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    padding: haupt ? '12px 18px' : '8px 14px',
                    borderRadius: '8px',
                    border: haupt ? 'none' : `1px solid ${themeColors.border}`,
                    background: haupt ? themeColors.primary : themeColors.background,
                    color: haupt ? '#fff' : themeColors.text,
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    fontSize: haupt ? '16px' : '14px',
                    flexBasis: haupt ? '100%' : 'auto',
                    textAlign: 'center',
                    boxSizing: 'border-box'
                  }}
                >
                  {l.name} →
                </a>
              );
            })}
          </div>
        </div>
      )}

      {/* Tracking Results */}
      {trackingInfo && (() => {
        const kachel = { background: themeColors.background, padding: '16px', borderRadius: '8px', border: `1px solid ${themeColors.border}` };
        const kopfzeile = { fontSize: '12px', color: themeColors.textSecondary, marginBottom: '4px' };
        const feld = (titel, wert, stil) => (wert || wert === 0 ? (
          <div style={kachel}>
            <div style={kopfzeile}>{titel}</div>
            <div style={{ fontWeight: 'bold', color: themeColors.text, ...stil }}>{wert}</div>
          </div>
        ) : null);
        return (
          <div data-testid="tracking-result" data-dienst={trackingInfo.dienst} style={{
            background: themeColors.cardBg,
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '20px',
            border: `1px solid ${themeColors.success}`,
            boxShadow: '0 4px 12px rgba(40, 167, 69, 0.1)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <FaCheckCircle style={{ color: themeColors.success, fontSize: '24px' }} />
              <h2 style={{ margin: 0, color: themeColors.text }}>{t('tracking.trackingInfo')}</h2>
            </div>

            <div style={{ display: 'grid', gap: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                {feld(t('tracking.trackingId'), trackingInfo.nummer, { wordBreak: 'break-all' })}
                {feld(t('tracking.carrier'), [trackingInfo.dienstName, trackingInfo.produkt].filter(Boolean).join(' · '))}
                <div style={kachel}>
                  <div style={kopfzeile}>{t('tracking.status')}</div>
                  <div style={{ fontWeight: 'bold', color: trackingInfo.zugestellt ? themeColors.success : themeColors.warning }}>
                    {trackingInfo.status || t('tracking.unknown')}
                  </div>
                  {trackingInfo.statusZeit && <div style={{ fontSize: '13px', color: themeColors.textSecondary, marginTop: '4px' }}>{formatZeit(trackingInfo.statusZeit)}</div>}
                </div>
                {feld(t('tracking.remainingDays'), trackingInfo.restTage, { fontSize: '22px', color: trackingInfo.restTage <= 1 ? themeColors.danger : themeColors.text })}
                {feld(t('tracking.parcelShop'), trackingInfo.shop)}
                {feld(t('tracking.origin'), trackingInfo.herkunft, { fontWeight: 'normal' })}
                {feld(t('tracking.destination'), trackingInfo.ziel, { fontWeight: 'normal' })}
              </div>

              {trackingInfo.link && (
                <div style={kachel}>
                  <div style={{ ...kopfzeile, marginBottom: '8px' }}>{t('tracking.serviceLink')}</div>
                  <a
                    href={trackingInfo.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: themeColors.primary, textDecoration: 'none', fontWeight: 'bold', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                  >
                    {t('tracking.toCarrierWebsite')}
                  </a>
                </div>
              )}

              {trackingInfo.ereignisse && trackingInfo.ereignisse.length > 0 && (
                <div>
                  <h3 style={{ margin: '0 0 16px 0', color: themeColors.text, fontSize: '18px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {t('tracking.events')}
                  </h3>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {trackingInfo.ereignisse.map((event, index) => (
                      <div
                        key={index}
                        style={{
                          background: themeColors.background,
                          padding: '16px',
                          borderRadius: '8px',
                          marginBottom: '12px',
                          border: `1px solid ${themeColors.border}`,
                          borderLeft: `4px solid ${themeColors.primary}`
                        }}
                      >
                        <div style={{ fontSize: '14px', fontWeight: 'bold', color: themeColors.text, marginBottom: '4px' }}>
                          {formatZeit(event.zeit)}
                        </div>
                        <div style={{ color: themeColors.textSecondary, fontSize: '14px' }}>
                          {event.text}{event.ort ? ` · ${event.ort}` : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default TrackingForm;

